import React, { useState } from 'react';
import { useParams } from 'react-router';

import { Alert, Button, Panel } from '@shared/components/bootstrap';
import { NavLinkButton } from '@shared/components/bootstrap';
import { Lightbox, Spacer, Timestamp } from '@shared/components/helpers';

import { InventoryGrid } from '@admin/components/inventory/grid';
import { Spinner } from '@admin/components/spinner';
import { thirdPartyTransferEditURL } from '@admin/config/routes';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  Status,
  ThirdPartyTransferFragment,
  ThirdPartyTransferStateEnum,
  useThirdPartyTransferCancelMutation,
  useThirdPartyTransferDetailsQuery,
} from '@admin/schema';

import { PageHeader } from '../helpers/page_header';
import { LongDistanceMoveSummary } from '../orders/long_distance_move_summary';
import { Breadcrumbs } from './breadcrumbs';
import { getStateBadgeClass } from './state';

function notNullImage<Image>(image: Image | null | undefined): image is Image {
  return image != null;
}

const ImagesPanel: React.FC<{ transfer: ThirdPartyTransferFragment }> = ({ transfer }) => {
  const [selection, setSelection] = useState<string | undefined>(undefined);
  const { outboundEmptyTrailerImage, outboundLoadedTrailerImage, outboundBillOfLadingImage } = transfer;

  const images = [outboundEmptyTrailerImage, outboundLoadedTrailerImage, outboundBillOfLadingImage].filter(
    notNullImage,
  );

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>Photos</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        <div className="grid">
          <div className="row">
            {images &&
              images
                .sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))
                .map((image) => (
                  <div key={image.id} className="entry col-xs-6 col-sm-4 col-md-3 col-lg-2">
                    <ul className="list-unstyled">
                      <li>{image.name}</li>
                      <li>
                        <Timestamp value={image.createdAt} format={Timestamp.Format.DateTime} />
                      </li>
                    </ul>
                    <figure className="brick-square">
                      <a
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setSelection(image.source);
                        }}
                      >
                        <img className="location-image" src={image.size} />
                      </a>
                    </figure>
                  </div>
                ))}
            {selection && <Lightbox imageURL={selection} onClose={() => setSelection(undefined)} />}
          </div>
        </div>
      </Panel.Body>
    </Panel>
  );
};

export const ThirdPartyTransferDetails: React.FC = () => {
  useTitle('Third-Party Transfer');
  const { accountID, thirdPartyTransferID } = useParams<{
    accountID: string;
    thirdPartyTransferID: string;
  }>();

  const { data: { thirdPartyTransfer } = {}, refetch } = useThirdPartyTransferDetailsQuery({
    client,
    variables: { id: thirdPartyTransferID },
  });

  const [execute, { loading: canceling, data }] = useThirdPartyTransferCancelMutation({
    client,
    onCompleted: ({ thirdPartyTransferCancel }) => {
      if (thirdPartyTransferCancel?.status === Status.Ok) {
        setShowConfirm(false);
        refetch();
      }
    },
  });

  const error = data?.thirdPartyTransferCancel?.error;
  const [showConfirm, setShowConfirm] = useState(false);

  const handleConfirm = () => execute({ variables: { id: thirdPartyTransferID } });

  if (!thirdPartyTransfer) {
    return <Spinner />;
  }

  const renderRow = (label: string, value: React.ReactNode) => (
    <div className="row mb-2">
      <div className="col-sm-1 text-muted">
        <strong>{label}</strong>
      </div>
      <div className="col-sm-2">{value}</div>
    </div>
  );

  return (
    <>
      <PageHeader>
        <Breadcrumbs thirdPartyTransfer={thirdPartyTransfer} />
      </PageHeader>

      <Panel>
        {error && <Alert style="danger">{error}</Alert>}

        <Panel.Body>
          {renderRow('Booking Platform', thirdPartyTransfer.bookingPlatform)}
          {renderRow('Booking Number', thirdPartyTransfer.bookingNumber)}
          {renderRow('Origin', thirdPartyTransfer.originWarehouse.name)}
          {renderRow('Destination', thirdPartyTransfer.destinationWarehouse.name)}
          {renderRow(
            'Pickup SLA',
            <>
              <div>{thirdPartyTransfer.formattedPickupDate}</div>
              {thirdPartyTransfer.formattedPickupTime && (
                <div className="text-secondary">{thirdPartyTransfer.formattedPickupTime}</div>
              )}
            </>,
          )}
          {renderRow(
            'Delivery SLA',
            <>
              <div>{thirdPartyTransfer.formattedDeliveryDate}</div>
              {thirdPartyTransfer.formattedDeliveryTime && (
                <div className="text-secondary">{thirdPartyTransfer.formattedDeliveryTime}</div>
              )}
            </>,
          )}
          {renderRow('Carrier', thirdPartyTransfer.carrier || '—')}
          {renderRow('Cost', thirdPartyTransfer.formattedCost)}
          {renderRow(
            'State',
            <span className={`badge ${getStateBadgeClass(thirdPartyTransfer.state)}`}>
              {thirdPartyTransfer.state.toUpperCase()}
            </span>,
          )}
          {thirdPartyTransfer.longDistanceMove && (
            <>
              <Spacer height="8px" />
              <LongDistanceMoveSummary move={thirdPartyTransfer.longDistanceMove} />
            </>
          )}
        </Panel.Body>

        <Panel.Footer align="right">
          {thirdPartyTransfer.state === ThirdPartyTransferStateEnum.Approved &&
            (showConfirm ? (
              <>
                <div>Are you sure you want to cancel this transfer?</div>
                <Button kind="danger" loading={canceling} onClick={handleConfirm}>
                  Yes
                </Button>
                <Button kind="primary" onClick={() => setShowConfirm(false)}>
                  No
                </Button>
              </>
            ) : (
              <Button kind="danger" disabled={canceling} onClick={() => setShowConfirm(true)}>
                Cancel
              </Button>
            ))}
          {!showConfirm && thirdPartyTransfer.state === ThirdPartyTransferStateEnum.Approved && (
            <NavLinkButton kind="primary" to={thirdPartyTransferEditURL(accountID, thirdPartyTransferID)}>
              Edit
            </NavLinkButton>
          )}
        </Panel.Footer>
      </Panel>

      <InventoryGrid path={`/third_party_transfers/${thirdPartyTransfer.id}/inventory.json`} />

      <ImagesPanel transfer={thirdPartyTransfer} />
    </>
  );
};
